/**
 * mapPrintColorSanitizer
 *
 * #621 — Print/PDF fails when the legend is open.
 *
 * html2canvas 1.x cannot parse modern CSS color functions (`color()`,
 * `color-mix()`, `oklch()`, `oklab()`, `lab()`, `lch()`, `hwb()`). EM3 theme
 * tokens are derived with `color-mix(in srgb, ...)`, which browsers serialise
 * into `color(srgb ...)` computed values. When html2canvas walks the cloned
 * legend it hits one of these and throws "Attempting to parse an unsupported
 * color function 'color'", silently killing the entire export.
 *
 * This module rewrites such computed colors into plain `rgb()`/`rgba()` (which
 * html2canvas understands) inside the cloned subtree, before html2canvas parses
 * it. The conversion is delegated to the browser via a 2D canvas, so any valid
 * CSS color the browser can render is resolved to concrete channel values.
 */

/** CSS color functions html2canvas 1.x cannot parse. */
const UNSUPPORTED_COLOR_FN = /(?:^|[\s(,])(?:color|color-mix|oklch|oklab|lab|lch|hwb)\(/i;

/**
 * Computed-style properties that can carry a color value (and therefore an
 * unsupported color function). Camel-case to match CSSStyleDeclaration access.
 */
export const COLOR_PROPERTIES = [
  'color',
  'backgroundColor',
  'borderTopColor',
  'borderRightColor',
  'borderBottomColor',
  'borderLeftColor',
  'outlineColor',
  'textDecorationColor',
  'columnRuleColor',
  'caretColor',
  'fill',
  'stroke',
] as const;

/**
 * True when a CSS value uses a color function html2canvas 1.x cannot parse.
 * Guards against substrings (e.g. "discolored") by requiring an opening paren.
 */
export function isUnsupportedColorFn(value: string): boolean {
  if (!value) return false;
  return UNSUPPORTED_COLOR_FN.test(value);
}

/**
 * Return a sanitized color: when the value uses an unsupported color function,
 * delegate to `resolve` (which converts it to rgb); otherwise pass it through.
 */
export function sanitizeColorValue(value: string, resolve: (v: string) => string): string {
  return isUnsupportedColorFn(value) ? resolve(value) : value;
}

/**
 * Build a resolver that converts any browser-renderable CSS color into an
 * `rgb()`/`rgba()` string using a 1x1 2D canvas. Returns the input unchanged if
 * a canvas context is unavailable (e.g. non-DOM env) so callers degrade safely.
 */
export function buildCanvasColorResolver(): (value: string) => string {
  const canvas = document.createElement('canvas');
  canvas.width = 1;
  canvas.height = 1;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    return (value: string) => value;
  }
  return (value: string): string => {
    try {
      ctx.clearRect(0, 0, 1, 1);
      ctx.fillStyle = '#000';
      ctx.fillStyle = value; // browser parses any valid CSS color
      ctx.fillRect(0, 0, 1, 1);
      const [r, g, b, a] = ctx.getImageData(0, 0, 1, 1).data;
      return `rgba(${r}, ${g}, ${b}, ${(a / 255).toFixed(3)})`;
    } catch {
      // If even the browser cannot resolve it, leave it for html2canvas to
      // handle rather than substituting a misleading colour.
      return value;
    }
  };
}

/**
 * Walk a cloned element subtree and rewrite any computed color that uses an
 * unsupported color function into a plain rgb value, applied as an inline style
 * (which html2canvas reads as the resolved computed value).
 *
 * Returns the number of properties rewritten (useful for diagnostics/tests).
 */
export function sanitizeClonedColors(root: HTMLElement): number {
  const view = root.ownerDocument?.defaultView;
  if (!view) return 0;
  const resolve = buildCanvasColorResolver();
  const elements: HTMLElement[] = [root, ...Array.from(root.querySelectorAll<HTMLElement>('*'))];
  let rewritten = 0;

  for (const el of elements) {
    const computed = view.getComputedStyle(el);
    for (const prop of COLOR_PROPERTIES) {
      const value = computed[prop as any] as string;
      if (isUnsupportedColorFn(value)) {
        el.style[prop as any] = resolve(value);
        rewritten++;
      }
    }
  }
  return rewritten;
}
